import { request } from '@/services/requestService';
import { format } from 'date-fns';
import { defineStore } from 'pinia';
import { DefaultResourcesY1S1, DefaultResourcesY1S2, DefaultResourcesY2S1, DefaultResourcesY2S2 } from '@/data/resourceData';
import { useResourceStore } from './resourceStore';
import { getOtherResourceStatusKey } from '@/helpers';
import { type ResourceStatusKey, type ResourcesByStatuses, resourceStatusKeys, getResourceByStatuses } from '@/interfaces/resourceStatusInterface';
import type { Ef } from '@/interfaces/efInterface';
import type { ResourceTypeKey } from '@/interfaces/resourceFilterInterface';
import type { SchoolResource, ResourceBase, Resource } from '@/interfaces/resourceInterface';
import { useUser } from '@/stores/userStore';

export const useDashboardStore = defineStore('dashboard', {
	state: () => {
		return {
			loading: false,
			resourcesByStatuses: {} as ResourcesByStatuses,
			resourceAddCounterIds: new Set<number>(),
			ef: null as Ef | null,
		};
	},
	getters: {
		resourceAddCounter(): number {
			return this.resourceAddCounterIds.size;
		},
		todoResources(state): SchoolResource[] {
			if (!Object.values(state.resourcesByStatuses).length) {
				return [];
			}

			return Object.values(state.resourcesByStatuses[resourceStatusKeys.Todo].resourcesByType).reduce<SchoolResource[]>((acc, rbt) => {
				acc.push(...rbt.resources);
				return acc;
			}, []);
		},
		todoResourceIds(): number[] {
			return this.todoResources.map((r) => r.ID);
		},
		hasAnyTodoResources(): boolean {
			return this.todoResources.length > 0;
		},
		hasAnyCompletedResources(state): boolean {
			if (!Object.values(state.resourcesByStatuses).length) {
				return false;
			}

			return Object.values(state.resourcesByStatuses[resourceStatusKeys.Completed].resourcesByType).some((r) => r.resources.length > 0);
		},
		hasAnyResources(): boolean {
			return this.hasAnyTodoResources || this.hasAnyCompletedResources;
		},
	},
	actions: {
		initDashboardStore() {
			this.initDashboardResources();
			this.initEf();
		},
		reInitDashboardStore() {
			this.clearStore();
			this.initDashboardStore();
		},
		clearStore() {
			this.ef = null;
			this.resourcesByStatuses = getResourceByStatuses();
		},
		async initEf() {
			const res = await request<{ ef: Ef }>('school', 'getEf');
			if (!res.success || !res.data.ef) {
				return;
			}

			this.ef = res.data.ef;
		},
		async initDashboardResources() {
			if (this.loading || this.hasAnyResources) {
				return;
			}

			this.loading = true;

			this.resourcesByStatuses = getResourceByStatuses();

			const res = await request<{ resources: SchoolResource[] }>('school', 'GetResources');

			// Clear this before the early return below, not after it: it also gates
			// re-entry at the top, and it's the dashboard's only render branch, so a
			// failed load that left it true would show a spinner forever.
			this.loading = false;

			if (!res.success || !res.data.resources) {
				return;
			}

			res.data.resources.forEach((r) => {
				const resourcesByStatus = Object.values(this.resourcesByStatuses).find((rbs) => {
					return rbs.key === r.completed_status;
				});

				// Fix for hiding bots in dashboard
				if (!resourcesByStatus) {
					return;
				}

				const resourcesInType = resourcesByStatus.resourcesByType[r.type];
				if (!resourcesInType) {
					return;
				}

				resourcesInType.resources.push(r);
			});
		},
		toggleResourceCompletion(resource: SchoolResource) {
			// The model has been updated already, but this resource still has old
			// properties, so these values are the old ones that need to be udpated
			const status = resource.completed_status;
			const type = resource.type;
			const resourceGroupRemovedFrom = this.resourcesByStatuses[status].resourcesByType[type].resources;
			const isMarkingComplete = !resource.completed_date;
			const user = useUser();

			if (isMarkingComplete) {
				resource.completed_date = format(new Date(), 'y-m-d H:i:s');
				resource.completed_by = user.email;
				resource.completed_status = resourceStatusKeys.Completed;
			} else {
				resource.completed_date = '';
				resource.completed_by = '';
				resource.completed_status = resourceStatusKeys.Todo;
			}

			const emptyTypeKey = resourceGroupRemovedFrom.length <= 0 ? resource.type : undefined;
			this.handleResourceListUpdate(emptyTypeKey);
		},
		async handleResourceListUpdate(emptyTypeKey?: ResourceTypeKey) {
			const resources = Object.values(this.resourcesByStatuses).reduce((rbsAcc, resourcesByStatus) => {
				const a = Object.values(resourcesByStatus.resourcesByType).reduce((rbaAcc, resourcesByType) => {
					rbaAcc.push(...resourcesByType.resources);
					return rbaAcc;
				}, [] as SchoolResource[]);

				rbsAcc.push(...a);
				return rbsAcc;
			}, [] as SchoolResource[]);

			const res = await request('school', 'UpdateResources', {
				resources,
				emptyTypeKey,
			});

			if (!res.success) {
				alert('There was an error, please refresh and try again. If the issue persists please contact your EF.');
			}
		},
		handleResourceToggleCompleted(resource: SchoolResource, resourceStatusKey: ResourceStatusKey) {
			const resourcesToRemoveFrom = this.resourcesByStatuses[resourceStatusKey].resourcesByType[resource.type].resources;
			const index = resourcesToRemoveFrom.indexOf(resource);
			resourcesToRemoveFrom.splice(index, 1);

			const newStatus = getOtherResourceStatusKey(resourceStatusKey);
			const resourcesToAddTo = this.resourcesByStatuses[newStatus].resourcesByType[resource.type].resources;
			resourcesToAddTo.push(resource);

			// this needs to happen after the resource has been moved to the different
			// status because we also fire this event after a drag, which is also after
			// the model change has happened. The resource still has old properties here
			// but is in the correct model
			this.toggleResourceCompletion(resource);
		},
		removeResource(resource: ResourceBase, resourceStatusKey: ResourceStatusKey) {
			this.resourceAddCounterIds.delete(resource.ID);
			const resources = this.resourcesByStatuses[resourceStatusKey].resourcesByType[resource.type].resources;
			const index = resources.findIndex((r) => r.ID === resource.ID);
			resources.splice(index, 1);
			this.handleResourceListUpdate();
		},
		addResource(resource: Resource, disableUpdate: boolean) {
			this.resourceAddCounterIds.add(resource.ID);
			const todos = this.resourcesByStatuses[resourceStatusKeys.Todo];
			const user = useUser();

			// todo
			// Try and type this
			todos!.resourcesByType[resource.type].resources.push({
				...resource,
				added_by: user.email,
				added_date: format(new Date(), 'y-m-d H:i:s'),
				completed_date: null,
				completed_by: null,
				completed_status: resourceStatusKeys.Todo,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} as any as SchoolResource);

			if (!disableUpdate) {
				this.handleResourceListUpdate();
			}
		},
		isInDashboardTodo(resourceId: number): boolean {
			return this.todoResourceIds.indexOf(resourceId) >= 0;
		},
		resetResourceAddCounter() {
			this.resourceAddCounterIds = new Set();
		},
		addYearResources(resourceListIds: number[]) {
			const resourceStore = useResourceStore();
			if (!resourceStore.resources.length) {
				return;
			}

			resourceListIds.forEach((resourceId) => {
				const resource = resourceStore.resources.find((r) => r.ID === resourceId);
				if (resource) {
					this.addResource(resource, true);
				}
			});

			this.handleResourceListUpdate();
		},
		addY1S1Resources() {
			this.addYearResources(DefaultResourcesY1S1);
		},
		addY1S2Resources() {
			this.addYearResources(DefaultResourcesY1S2);
		},
		addY2S1Resources() {
			this.addYearResources(DefaultResourcesY2S1);
		},
		addY2S2Resources() {
			this.addYearResources(DefaultResourcesY2S2);
		},
	},
});
